import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calculator, FilePlus2, Presentation } from 'lucide-react';
import { offerApi } from '../../../services/offerApi';
import { formatEuro } from '../../../utils/offerCalc';
import NewDocumentDialog from '../../Offers/NewDocumentDialog';
import { Card } from '../ui';

const day = (value) => (value ? new Date(value).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '');

// Chiffrages et propositions du projet, créés d'ici sans repasser par la page Offres (demande de Rémy, 2026-10-12).
const ProjectOffers = ({ projectId, projectName }) => {
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [creating, setCreating] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    Promise.all([offerApi.list({ project_id: projectId, limit: 50 }), offerApi.proposals({ project_id: projectId, limit: 50 })])
      .then(([estimateData, proposalData]) => {
        setEstimates(estimateData.items || []);
        setProposals(proposalData.items || []);
      })
      .catch((err) => setError(err.message));
  }, [projectId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    offerApi.permissions().then((data) => setCanEdit(Boolean(data.can_edit))).catch(() => setCanEdit(false));
  }, []);

  const rows = [
    ...estimates.map((row) => ({ ...row, kind: 'chiffrage', path: `/tools/offres/${row.id}` })),
    ...proposals.map((row) => ({ ...row, kind: 'proposition', path: `/tools/offres/propositions/${row.id}` })),
  ].sort((a, b) => String(b.updated_at || '').localeCompare(String(a.updated_at || '')));

  const actions = canEdit && (
    <div className="flex flex-wrap gap-2">
      <button type="button" onClick={() => setCreating('estimate')}
        className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700">
        <FilePlus2 className="h-3.5 w-3.5" /> Nouveau chiffrage
      </button>
      <button type="button" onClick={() => setCreating('proposal')}
        className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
        <Presentation className="h-3.5 w-3.5" /> Nouvelle proposition
      </button>
    </div>
  );

  return (
    <Card title="Chiffrages et propositions" icon={Calculator} action={actions}>
      {error && <p className="text-sm text-rose-600">{error}</p>}
      {rows.length === 0 ? (
        <p className="text-sm text-slate-500">
          Aucun chiffrage ni aucune proposition{canEdit ? ' : « Nouveau chiffrage » ouvre l\'affaire du projet si elle n\'existe pas encore.' : '.'}
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {rows.map((row) => (
            <li key={`${row.kind}-${row.id}`}>
              <button type="button" onClick={() => navigate(row.path)}
                className={`flex w-full flex-wrap items-center gap-x-3 gap-y-1 py-2.5 text-left hover:bg-slate-50 ${row.archived ? 'opacity-50' : ''}`}>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${row.kind === 'chiffrage' ? 'bg-blue-50 text-blue-700' : 'bg-violet-50 text-violet-700'}`}>
                  {row.kind}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900">{row.title}</span>
                <span className="text-xs text-slate-500">{row.affaire_number || ''}</span>
                {row.kind === 'chiffrage' && <span className="text-sm font-semibold tabular-nums text-blue-700">{formatEuro(row.total_net)}</span>}
                <span className="text-xs text-slate-400">v{row.revision_number} · {day(row.updated_at)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {creating && (
        <NewDocumentDialog kind={creating} project={{ id: Number(projectId), name: projectName }}
          onClose={() => setCreating(null)} onCreated={(path) => navigate(path)} />
      )}
    </Card>
  );
};

export default ProjectOffers;
